import { GetterTree } from 'vuex';
import { State } from '@/store';
import { Pricing } from '@/app/domain/Pricing';
import { Setting } from '@/app/domain/Setting';
import { Company } from '@/app/domain/Company';
import { Contact } from '@/app/domain/Contact';
import { EmailNotificationsSettings } from '@/app/domain/EmailNotificationsSettings';
import { ConsumerProfile } from '@/app/domain/ConsumerProfile';
import { ModuleState } from './state';
import { UserPermissions, Permissions } from '../services/UserPermissionsService';
import { TenantLicense } from '../services/DeleteAccountService';

export function createGetters(): GetterTree<ModuleState, State> {
  return {
    isWizardFetching(state: ModuleState): boolean {
      return state.isWizardFetching;
    },
    isWizardAvailable(state: ModuleState): boolean {
      return state.isWizardAvailable;
    },
    pricing(state: ModuleState): Pricing | null {
      return state.pricing;
    },
    isPricingFetching(state: ModuleState): boolean {
      return state.isPricingFetching;
    },
    setting(state: ModuleState): Setting | null {
      return state.setting;
    },
    isSettingFetching(state: ModuleState): boolean {
      return state.isSettingFetching;
    },
    isProfileFetching(state: ModuleState): boolean {
      return state.isProfileFetching;
    },
    selfServiceMode(state: ModuleState): boolean {
      return state.selfServiceMode;
    },
    isSelfServiceModeFetching(state: ModuleState): boolean {
      return state.isSelfServiceModeFetching;
    },
    company(state: ModuleState): Company {
      return state.company;
    },
    isCompanyFetching(state: ModuleState): boolean {
      return state.isCompanyFetching;
    },
    isCompanyEditing(state: ModuleState): boolean {
      return state.isCompanyEditing;
    },
    addedContactList(state: ModuleState): Contact[] {
      return state.addedContactList;
    },
    contactList(state: ModuleState): Contact[] {
      return state.contactList;
    },
    notAddedContactList(state: ModuleState): Contact[] {
      return state.notAddedContactList;
    },
    isContactListFetching(state: ModuleState): boolean {
      return state.isContactListFetching;
    },
    isTenantContactListFetching(state: ModuleState): boolean {
      return state.isTenantContactListFetching;
    },
    tenantContactList(state: ModuleState): Contact[] {
      return state.tenantContactList;
    },
    isDirectPartnerFetching(state: ModuleState): boolean {
      return state.isDirectPartnerFetching;
    },
    isDirectPartner(state: ModuleState): boolean {
      return state.isDirectPartner;
    },
    isContactCreating(state: ModuleState): boolean {
      return state.isContactCreating;
    },
    isContactEditing(state: ModuleState): boolean {
      return state.isContactEditing;
    },
    isContactDeleting(state: ModuleState): boolean {
      return state.isContactDeleting;
    },
    isVerificationEmailSending(state: ModuleState): boolean {
      return state.isVerificationEmailSending;
    },
    consumerProfile(state: ModuleState): ConsumerProfile {
      return state.consumerProfile;
    },
    isConsumerProfileFetching(state: ModuleState): boolean {
      return state.isConsumerProfileFetching;
    },
    isConsumerProfileEditing(state: ModuleState): boolean {
      return state.isConsumerProfileEditing;
    },
    emailNotificationsSettings(state: ModuleState): EmailNotificationsSettings {
      return state.emailNotificationsSettings;
    },
    isEmailNotificationsSettingsFetching(state: ModuleState): boolean {
      return state.isEmailNotificationsSettingsFetching;
    },
    isEmailNotificationsSettingsEditing(state: ModuleState): boolean {
      return state.isEmailNotificationsSettingsEditing;
    },
    userPermissions(state: ModuleState): UserPermissions {
      return state.userPermissions;
    },
    hasUserPermissions:
      (state: ModuleState, getters: {userPermissions: UserPermissions}) => (permission: Permissions) => {
        return getters.userPermissions.includes(permission);
    },
    isTenantLicensesFetching(state: ModuleState): boolean {
      return state.isTenantLicensesFetching;
    },
    tenantLicenses(state: ModuleState): TenantLicense[] {
      return state.tenantLicenses;
    },
    isTenantStorageUsageFetching(state: ModuleState): boolean {
      return state.isTenantStorageUsageFetching;
    },
    tenantStorageUsage(state: ModuleState): number {
      return state.tenantStorageUsage;
    },
    isDeleteAccountConfirmationStatusFetching(state: ModuleState): boolean {
      return state.isDeleteAccountConfirmationStatusFetching;
    },
    deleteAccountConfirmationStatus(state: ModuleState): string {
      return state.deleteAccountConfirmationStatus;
    },
    customerType(state: ModuleState): string {
      return state.customerType;
    },
    isEditModeEnabled(
      state: ModuleState,
      getters: {selfServiceMode: boolean, userPermissions: UserPermissions},
      rootState: State,
      rootGetters: GetterTree<State, State>
    ): boolean {
      const isOwnTenant: boolean = rootGetters['stateSync/selfTenantUuid'] === rootGetters['stateSync/tenantUuid'];
      const isTenantEditable: boolean = !isOwnTenant ? !getters.selfServiceMode : true;

      return isTenantEditable && getters.userPermissions.includes(Permissions.edit);
    }
  };
}
